import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AnalyticsEvent {
  id: string;
  event_type: string;
  page_path: string | null;
  referrer: string | null;
  user_agent: string | null;
  ip_address: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

export interface TrackEventData {
  event_type: string;
  page_path?: string;
  referrer?: string;
  metadata?: Record<string, unknown>;
}

// Hook para registrar evento de analytics (público)
export function useTrackEvent() {
  return useMutation({
    mutationFn: async (data: TrackEventData) => {
      const { error } = await supabase.from("analytics_events" as never).insert({
        event_type: data.event_type,
        page_path: data.page_path || window.location.pathname,
        referrer: data.referrer || document.referrer || null,
        user_agent: navigator.userAgent,
        metadata: data.metadata || null,
      } as never);

      if (error) throw error;
    },
  });
}

// Hook para obter estatísticas de analytics (admin)
export function useAnalyticsStats(days: number = 30) {
  return useQuery({
    queryKey: ["analytics-stats", days],
    queryFn: async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from("analytics_events" as never)
        .select("*")
        .gte("created_at", startDate.toISOString())
        .order("created_at", { ascending: false });

      if (error) throw error;

      const events = data || [];

      // Agrupar por tipo de evento
      const byEventType = events.reduce(
        (acc, event) => {
          acc[event.event_type] = (acc[event.event_type] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      // Agrupar por página
      const byPage = events.reduce(
        (acc, event) => {
          if (event.page_path) {
            acc[event.page_path] = (acc[event.page_path] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>,
      );

      // Agrupar por dia
      const byDay = events.reduce(
        (acc, event) => {
          const date = new Date(event.created_at).toISOString().split("T")[0];
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );

      // Top páginas
      const topPages = Object.entries(byPage)
        .filter(([path]) => !path.startsWith("/admin"))
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 10)
        .map(([path, views]) => ({ path, views: views as number }));

      // Timeline de eventos para gráfico
      const timeline = Object.entries(byDay)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([date, count]) => ({ date, count }));

      // Page views por dia nos últimos 14 dias (zeros incluídos)
      const pvByDay = events.reduce(
        (acc, event) => {
          if (event.event_type !== "page_view") return acc;
          const date = new Date(event.created_at).toISOString().split("T")[0];
          acc[date] = (acc[date] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      );
      const pageViewsByDay: { date: string; count: number }[] = [];
      for (let i = 13; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        const key = d.toISOString().split("T")[0];
        pageViewsByDay.push({ date: key, count: pvByDay[key] || 0 });
      }

      // Parse device types from user_agent
      const deviceBreakdown = events.reduce(
        (acc, event) => {
          const ua = (event.user_agent || "").toLowerCase();
          if (/ipad|tablet|kindle|silk|playbook/i.test(ua)) {
            acc.tablet++;
          } else if (/mobile|iphone|android|ipod|blackberry|opera mini|iemobile/i.test(ua)) {
            acc.mobile++;
          } else {
            acc.desktop++;
          }
          return acc;
        },
        { desktop: 0, mobile: 0, tablet: 0 },
      );

      return {
        totalEvents: events.length,
        pageViews: byEventType.page_view || 0,
        formSubmits: byEventType.form_submit || 0,
        ctaClicks: byEventType.cta_click || 0,
        byEventType,
        topPages,
        timeline,
        pageViewsByDay,
        deviceBreakdown,
      };
    },
  });
}

// Hook para registrar page view
export function usePageView() {
  const trackEvent = useTrackEvent();

  return (pagePath?: string) => {
    trackEvent.mutate({
      event_type: "page_view",
      page_path: pagePath || window.location.pathname,
    });
  };
}
